import clsx from "clsx";

import dayjs from "dayjs";
import { navigate } from "gatsby";
import { ChangeEvent, useEffect, useState } from "react";
import useSessionStorage from "@/base/hooks/useSessionStorage";
import { searchValueCheck } from "@/base/utils/searchValueCheck";
import Button from "@/components/elements/Button";
import Heading from "@/components/elements/Heading";
import Input from "@/components/elements/Input";
import LinkComponent from "@/components/elements/LinkComponent";
import RemarkRichTextComponents from "@/components/elements/RichText";
import { useLocation } from "@reach/router";
import SearchAndSocials from "../SearchAndSocials";

import * as styles from "./styles.module.scss";
import Image from "@/components/elements/Image";
import ImagePlaceholder from "@/components/elements/ImagePlaceholder";

interface MobileMenuProps {
  main_menu?: HeaderTemplate["main_menu"];
  socials_menu?: HeaderTemplate["socials_menu"];
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isMenuOpen: boolean) => void;
  logo: HeaderTemplate["logo"];

  isNestedNavVisible: boolean | string;
  setIsNestedNavVisible: (isNestedNavVisible: boolean | string) => void;
  currentGrant:
    | (StoryblokStory & {
        field_gr_date_string: string;
        content: { gr_date: string };
      })
    | null;
}

export function MobileMenu({
  main_menu,
  socials_menu,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  logo,
  isNestedNavVisible,
  setIsNestedNavVisible,
  currentGrant,
}: MobileMenuProps) {
  const [searchValue, setSearchValue] = useState("");

  const [, setValue] = useSessionStorage<string>("alpro_searchPage", "0");

  useEffect(() => {
    const appHeight = function () {
      const doc = document.documentElement;
      doc.style.setProperty("--app-height", `${window.innerHeight * 0.01}px`);
    };
    window.addEventListener("resize", appHeight);
    appHeight();
    return () => window.removeEventListener("resize", appHeight);
  }, []);

  const isGrantAvailable = (link: MegaMenuComponent) => !!currentGrant && link.label === "Research support";

  const location = useLocation();
  const hideLogo = location.pathname.includes("call-for-research-grant-applications");

  const renderCloseMenuButton = () => {
    return (
      <button
        className={clsx(styles.hamburger, {
          [styles.isActive]: isMobileMenuOpen,
        })}
        aria-label={`button ${isMobileMenuOpen}`}
        onClick={() => {
          setIsMobileMenuOpen(!isMobileMenuOpen);
          setIsNestedNavVisible(false);
        }}
      >
        <span className={styles.hamburgerBox}>
          <span className={styles.hamburgerInner}></span>
        </span>
      </button>
    );
  };

  const handleSubmit = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchValueCheck(searchValue)) {
      navigate(`/search?query=${encodeURIComponent(searchValue)}`);
      setValue("0");
      setIsNestedNavVisible(false);
      setIsMobileMenuOpen(false);
      setSearchValue("");
    }
  };

  return (
    <>
      <div
        className={clsx(
          styles.mobileNavbar,
          {
            [styles.isActive]: isMobileMenuOpen,
          },
          "mobile-nav-container"
        )}
      >
        {Array.isArray(main_menu) && isMobileMenuOpen && (
          <>
            {main_menu.map(
              (link) =>
                link.component !== "element_link" && (
                  <ul
                    key={link._uid}
                    className={clsx(styles.submenu, {
                      [styles.isVisible]: link._uid === isNestedNavVisible,
                      [styles.withGrant]: isGrantAvailable(link),
                    })}
                  >
                    <Image
                      className={clsx(styles.mobileBackground, {
                        [styles.isActive]: isMobileMenuOpen,
                      })}
                      options={{ width: 188, height: 139 }}
                      fixed
                      data={{ image: "/svgs/navbarMobileBg.svg" } as StoryblokGatsbyImageAsset}
                      alt="background"
                      data-svg="true"
                    />
                    <li className={styles.submenuHeader}>
                      <div>
                        <button type="button" onClick={() => setIsNestedNavVisible(false)}>
                          <img src="/svgs/mobileNavArrow.svg" alt="arrow" data-svg="true" />
                        </button>{" "}
                        <p>{link.label}</p>
                        {renderCloseMenuButton()}
                      </div>
                    </li>
                    {link.options && (
                      <>
                        {link.options.map((item) => (
                          <li key={item.label}>
                            <div className={styles.submenuContent}>
                              <RemarkRichTextComponents content={item.content} linkWithIcon />
                            </div>
                          </li>
                        ))}
                        {isGrantAvailable(link) && currentGrant && (
                          <li>
                            <div>
                              <div>
                                <Heading level={3}>We have open grants right now</Heading>
                                <p>🎯 Deadline: {dayjs(currentGrant?.content?.gr_date).format("DD MMMM YYYY")}</p>
                              </div>

                              <LinkComponent type="internal" url={`/${currentGrant.full_slug}`} with_icon>
                                Read more about research grants
                              </LinkComponent>
                            </div>
                          </li>
                        )}
                      </>
                    )}
                  </ul>
                )
            )}
            <ul
              className={clsx(styles.submenu, {
                [styles.isVisible]: isNestedNavVisible === "search",
              })}
            >
              <img
                className={clsx(styles.mobileBackground, {
                  [styles.isActive]: isMobileMenuOpen,
                })}
                src="/svgs/navbarMobileBg.svg"
                alt="background"
                data-svg="true"
              />
              <li className={styles.submenuHeader}>
                <div>
                  <button type="button" onClick={() => setIsNestedNavVisible(false)}>
                    <img src="/svgs/mobileNavArrow.svg" alt="arrow" data-svg="true" />
                  </button>{" "}
                  <p>Search</p>
                  {renderCloseMenuButton()}
                </div>
              </li>
              <li className={styles.submenuSearch}>
                <form onSubmit={handleSubmit}>
                  <Input
                    type="text"
                    placeholder="Search..."
                    name="search"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    withIcon={
                      <img
                        src="/svgs/search.svg"
                        alt="search"
                        data-svg="true"
                        className={styles.searchIcon}
                        onClick={() => {
                          navigate(`/search?query=${encodeURIComponent(searchValue)}`);
                          setIsNestedNavVisible(false);
                          setIsMobileMenuOpen(false);
                          setSearchValue("");
                          setValue("0");
                        }}
                      />
                    }
                  />

                  <Button variant="primary" type="submit">
                    Search
                  </Button>
                </form>
              </li>
            </ul>
          </>
        )}
        <div className={styles.headerNavbar}>
          <LinkComponent url="/" type="internal" className={styles.logo}>
            <Image
              data={logo?.filename as StoryblokGatsbyImageAsset}
              alt={logo?.alt}
              fixed
              options={{ width: 120, height: 55.5 }}
              data-hidden={String(!isMobileMenuOpen && hideLogo)}
              className={styles.logoWrapper}
            />
          </LinkComponent>

          {renderCloseMenuButton()}
        </div>
        {Array.isArray(main_menu) && main_menu.length > 0 && isMobileMenuOpen && (
          <>
            <ul className={styles.menu}>
              {main_menu.map((link) =>
                link.component === "element_link" ? (
                  <li key={link.title}>
                    <LinkComponent url={link.url} type={link.type} className={styles.menuLink}>
                      {link.title}
                    </LinkComponent>
                  </li>
                ) : (
                  <li key={link.label}>
                    <button
                      type="button"
                      onClick={() => setIsNestedNavVisible(link._uid)}
                      className={styles.submenuButton}
                    >
                      {link.label} <img src="/svgs/mobileNavArrow.svg" alt="arrow" data-svg="true" />
                    </button>
                  </li>
                )
              )}
            </ul>
            <SearchAndSocials socials_menu={socials_menu} onSearchClick={() => setIsNestedNavVisible("search")} />
            <div className={styles.newsletter}>
              <Button variant="outlined" icon="newsletter" isIconLeft onClick={() => navigate("/newsletter")}>
                Sign up
              </Button>
            </div>
          </>
        )}
      </div>
      <img
        className={clsx(styles.mobileBackground, {
          [styles.isActive]: isMobileMenuOpen,
        })}
        src="/svgs/navbarMobileBg.svg"
        alt="background"
        data-svg="true"
      />
    </>
  );
}
